
import { defineComponent, ref, computed } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { mapActions, mapGetters, mapState } from "vuex";
import Loader from "@/components/shared/Loader.vue";
import Hero from "@/components/shared/layout/Hero.vue";
import StudentChoiceHeader from "@/components/shared/layout/StudentChoiceHeader.vue";
import StudentChoiceFooter from "@/components/shared/layout/StudentChoiceFooter.vue";
import Feedback from "@/components/shared/feedback/Feedback.vue";
import SupportModal from "@/components/modals/SupportModal.vue";
import useFormatter from "@/use/formatter";
import useApplications from "@/use/application";
import useLender from "@/use/lender";

export default defineComponent({
  setup() {
    const store = useStore();
    const route = useRoute();
    const { formatHexToRgb } = useFormatter();
    const { getLenderConfigurationByReferenceId } = useApplications();

    const showSupportModal = ref<boolean>(false);
    const loading = ref<boolean>(true);
		const appLenderId = computed(() => store.state?.appData?.application?.lenderId);
    const applicationsModule = computed(() => store.state.applicationsModule);
    const branding = computed(() => store.getters["applicationsModule/Branding"]);
		
		const textColor = computed(() => store.getters["applicationsModule/TextColor"]);

    function setLoading(flag: boolean) {
      loading.value = flag;
    }

    function getLender() {
      const routesThatUseBranding = ["Application", "ApplicationDocuments"];

      if (route.matched[0]?.name && routesThatUseBranding.includes(route.matched[0]?.name.toString())) {
        return store.state?.appData?.application?.lenderId || 0;
      }

      return route.query.lid || 0;
    }

    const { lenderId } = useLender(() => getLender());

    return {
      formatHexToRgb,
      route,
      getLenderConfigurationByReferenceId,
      showSupportModal,
      store,
      applicationsModule,
      branding,
      lenderId,
      loading,
      setLoading,
			textColor,
			appLenderId
    };
  },
  props: {
    externalLinks: {
      type: Boolean,
      default: false,
    },
    pullConfiguration: {
      type: Boolean,
      default: true,
    },
  },
  components: { Loader, Hero, StudentChoiceHeader, StudentChoiceFooter, Feedback, SupportModal },
  computed: {
    ...mapState(["applications", "idleVue", "lenderData", "featureFlags", "configuration"]),
    ...mapGetters("oidcStore", ["oidcUser", "oidcIsAuthenticated"]),
    themeStyle(): any {
      if (this.branding) {
        const themeRgb = this.formatHexToRgb(this.branding?.primaryColor);
        const secondaryRgb = this.formatHexToRgb(this.branding?.secondaryColor);
        return {
          "--theme-color": this.branding?.primaryColor || this.lenderData?.primaryColor,
          "--theme-color-rgb": `${themeRgb.r}, ${themeRgb.g}, ${themeRgb.b}`,
					"--theme-color-text": this.textColor,
          "--secondary-color": this.branding?.secondaryColor || this.lenderData?.secondaryColor,
          "--secondary-color-rgb": `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`,
        };
      }

      const themeRgb = this.formatHexToRgb(this.lenderData?.primaryColor);
      const secondaryRgb = this.formatHexToRgb(this.lenderData?.secondaryColor);
      return {
        "--theme-color": this.lenderData?.primaryColor,
        "--theme-color-rgb": `${themeRgb.r}, ${themeRgb.g}, ${themeRgb.b}`,
        "--secondary-color": this.lenderData?.secondaryColor,
        "--secondary-color-rgb": `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`,
      };
    },
    lid(): number {
      return (this.lenderData as any).lid || this.$route.query?.lid || 0;
    },
  },
  methods: {
    ...mapActions([
      "setApplications",
      "setLenderData",
      "setDefaultLender",
      "setFeatureFlags",
      "setConfiguration",
    ]),
  },
  async mounted() {
    const query = this.route.query as any;

    // pass LID query param
    let lid = query.lid || query.LID || 0;

    if (lid === 0) {
      lid = localStorage.getItem("slc-lid") || 0;
    }

    if (
      this.oidcUser !== null &&
      this.lenderData === undefined &&
      this.$route.params?.referenceId
    ) {
      this.getLenderConfigurationByReferenceId(
        this.oidcUser.sub,
        this.$route.params?.referenceId as string,
      )
        .then(async (result: any) => {
          if (result.lenderData) {
            await this.setLenderData(result.lenderData);
            localStorage.setItem("slc-lid", lid);
          } else {
            await this.setDefaultLender();
          }
          if (result.borrowerApplicationUrl && result.docUploadUrl) {
            const newConfig = {
              ...this.configuration,
              ...result,
              borrowerApplicationUrl: result.borrowerApplicationUrl,
              docUploadUrl: `${result.docUploadUrl}/api/v1/DocUploadService`,
              identityUrl: result?.identityUrl,
              payOffSample: result?.payOffSample,
              loanAccountSample: result?.loanAccountSample,
							collegeCounselorSchedulerUrl: result?.collegeCounselorSchedulerUrl,
            };
            this.setConfiguration(newConfig);
          }

          // In the case a page's content is dependent on configuration data
          this.loading = false;

          this.$emit("configured", true);
        })
        .catch((err: any) => {
          console.log("ERROR GETTING CONFIG", err);
          // set default lender settings
          this.setDefaultLender();
          this.loading = false;
        });
    } else if (this.lenderData === undefined || !this.$route.params?.referenceId) {
      // When a user hits a non-authenticated page we need default settings
      this.setDefaultLender();
      this.loading = false;
    } else {
      // Fallback to clear loader when user is already in app and navigating pages
      this.loading = false;
    }
  },
});
